import { MAX_BATCHES_PER_FIELD_COUNT, RESULTS_PER_BATCH_COUNT } from '@core/core/store'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { WellId } from 'common/types'

import type {
  KsuCompany,
  KsuField,
  KsuLicenseArea,
  OverallSearchQuery,
  OverallSearchResultCategory,
  OverallSearchState,
  PageDestination,
  SearchInCategoryResult,
  UploadedFileStatusType,
} from '../../types'
import { gotoPageInCategory, searchFromDbSources, searchInCategory } from './overallSearchActions'

const initialState: OverallSearchState = {
  query: { mode: 'geodb' },
  ksuCompanies: [],
  result: [],
  status: 'wait',
}

export const overallSearchSlice = createSlice({
  name: 'overallSearch',
  initialState,
  reducers: {
    updateSearchQuery(state, action: PayloadAction<OverallSearchQuery>) {
      const query = action.payload
      const stateQuery = state.query
      stateQuery.mode = query.mode
      stateQuery.globalSearch = query.globalSearch
      query.crms && (stateQuery.crms = query.crms)
      query.geodb && (stateQuery.geodb = query.geodb)
      query.geomate && (stateQuery.geomate = query.geomate)
    },
    setSearchResults(state, action: PayloadAction<OverallSearchResultCategory[]>) {
      state.result = action.payload
    },
    clearSearchResults(state) {
      state.result = []
      state.activeCategoryId = undefined
      state.categoryResult = undefined
      state.categoryPagination = undefined
    },
    setKsuCompanyEntities(state, action: PayloadAction<KsuCompany[] | undefined>) {
      state.ksuCompanies = action.payload ?? []
    },
    updateKsuCompanyEntity(
      state,
      action: PayloadAction<{
        companyId: string
        fields: KsuField[]
        licenseAreas: KsuLicenseArea[]
      }>,
    ) {
      const payload = action.payload
      const company = state.ksuCompanies.find(company => company.ksuUid === payload.companyId)
      if (company) {
        company.fields = payload.fields
        company.licenseAreas = payload.licenseAreas
      }
    },
    setSearchInCategoryResults(state, action: PayloadAction<SearchInCategoryResult | undefined>) {
      state.categoryResult = action.payload
    },
    updateKsuWellsState(
      state,
      action: PayloadAction<{
        wellIds: WellId[]
        state: UploadedFileStatusType
      }>,
    ) {
      const { wellIds, state: actionState } = action.payload
      const result = state.categoryResult
      if (result?.source === 'geodb') {
        result.wells
          .filter(well => wellIds.includes(well.wellUid))
          .forEach(well => {
            well.state = actionState
          })
      }
    },
    setActiveCategoryId(state, action: PayloadAction<string | undefined>) {
      state.activeCategoryId = action.payload
    },
    initCategoryPagination(state, action: PayloadAction<number | undefined>) {
      const rowCount = action.payload
      if (rowCount !== undefined && rowCount > RESULTS_PER_BATCH_COUNT) {
        state.categoryPagination = {
          rowCount,
          pageCount: Math.ceil(rowCount / RESULTS_PER_BATCH_COUNT),
          page: {
            page: 1,
            firstRow: 0,
            rows: RESULTS_PER_BATCH_COUNT,
          },
          prevPage: {
            page: 1,
            firstRow: 0,
            rows: RESULTS_PER_BATCH_COUNT,
          },
        }
      } else {
        state.categoryPagination = undefined
      }
    },
    setCategoryPage(state, action: PayloadAction<PageDestination>) {
      // Есть ли пагинация
      const prev = state.categoryPagination
      if (prev === undefined) return

      // Новый номер страницы
      const payload = action.payload
      let page = prev.page.page
      if (payload === 'next') {
        page = Math.min(page + 1, prev.pageCount)
      } else if (payload === 'prev') {
        page = Math.max(page - 1, 1)
      } else {
        page = Math.max(Math.min(payload, prev.pageCount), 1)
      }

      // Изменился ли он вообще
      if (prev.page.page === page) return

      // Вычисляем строки страницы с учетом перехлеста
      const firstSkipRow = RESULTS_PER_BATCH_COUNT * page
      const firestTakeRow = firstSkipRow - RESULTS_PER_BATCH_COUNT * MAX_BATCHES_PER_FIELD_COUNT
      const firstPageRow = Math.max(firestTakeRow, 0)

      state.categoryPagination = {
        rowCount: prev.rowCount,
        pageCount: prev.pageCount,
        page: {
          page,
          firstRow: firstPageRow,
          rows: firstSkipRow - firstPageRow,
        },
        prevPage: prev.page,
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchFromDbSources.pending, state => {
        state.status = 'loading'
      })
      .addCase(searchFromDbSources.fulfilled, state => {
        state.status = 'done'
      })
      .addCase(searchFromDbSources.rejected, state => {
        state.status = 'failed'
      })
      .addCase(searchInCategory.pending, state => {
        state.status = 'loading'
      })
      .addCase(searchInCategory.fulfilled, state => {
        state.status = 'done'
      })
      .addCase(searchInCategory.rejected, state => {
        state.status = 'failed'
      })
      .addCase(gotoPageInCategory.pending, state => {
        state.status = 'loading'
      })
      .addCase(gotoPageInCategory.fulfilled, state => {
        state.status = 'done'
      })
      .addCase(gotoPageInCategory.rejected, state => {
        state.status = 'failed'
      })
  },
})

export default overallSearchSlice.reducer
